import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Tooltip, IconButton, Typography, Fab } from '@material-ui/core';
import { Add, Refresh, Delete, CloudDownload, CloudUpload } from '@material-ui/icons';
import * as actions from './store/deviceDependentSettingActions';
import * as action from '../../np-core/Auth/store/authActions';
import { snackActions } from '../../sc-utilities/SnackBar';
import { hasPermission, BackButton, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import { MuiTableRows } from '../../../themes';
import Toolbar from '../../sc-utilities/Toolbar';
import DeviceDependentSettingDelete from './DeviceDependentSettingDelete';
import { CancelToken } from 'axios';
import config from "../../../config/config";
import {download} from "../../sc-utilities/download.utils";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        paddingTop: '4px',
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
        paddingTop: '4px'
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const DeviceDependentSettingList = props => {
    const { classes, tableColumns, redirectHandler, onFetchDeviceDependentSettingsCount, fetching, onBulkDeleteDeviceDependentSettings, onFetchDeviceDependentSettings, goToUploadDeviceDependentSetting, saveColumnsFilter, translations, activeCustomerID, permissions, deleting, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, deviceDependentSettingsID: null, deviceDependentSettingsIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const deletePermission = hasPermission(permissions, 'device_dependent_setting_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['device_dependent_setting_delete']),
        selectionProps: rowData => {
            return (deletePermission) ? {
                id: `checkbox_${rowData.id}`
            } :
                {
                    disabled: true,
                    checked: false,
                    id: `checkbox_${rowData.id}`
                }
        },
        search: false,
        minBodyHeight: window.innerHeight - 238 < 300 ? 300 : window.innerHeight - 238,
        maxBodyHeight: window.innerHeight - 238,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        exportText: 'Device Dependent Setting',
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchDeviceDependentSettingsCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchDeviceDependentSettings(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography >{translations['device_dependent_settings'] || 'Device dependent settings'}</Typography>
                {
                    hasPermission(permissions, 'device_dependent_setting_create') ?
                        <Fab
                            id="create_device_dependent_setting"
                            variant="extended"
                            size="small"
                            color="primary"
                            aria-label="Add"
                            className={classes.fab}
                            style={{ borderRadius: '1px' }}
                            onClick={() => redirectHandler(`/admin/deviceDependentSettingsCreate`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton
                        aria-label={translations["refresh"] || "Refresh"}
                        component="div" disabled={fetching}
                        onClick={() => tableRef.current && tableRef.current.onQueryChange()}
                    >
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {
                hasPermission(permissions, 'device_dependent_setting_export') ?
                    <Tooltip title={translations["dependent_json"] || "Download dependent setting (JSON)"}>
                        <IconButton
                            id={'download_json'} component={'div'}
                            aria-label={translations["dependent_json"] || "Download dependent setting (JSON)"}
                            value='json'
                            onClick={handleDownload}
                        >
                            <CloudDownload />
                        </IconButton>
                    </Tooltip> : null
            }
            {
                hasPermission(permissions, 'device_dependent_setting_export') ?
                    <Tooltip title={translations["dependent_xlsx"] || "Download dependent setting (XLSX)"}>
                        <IconButton
                            id={'download_xlsx'}
                            component={'div'}
                            aria-label={translations["dependent_xlsx"] || "Download dependent setting (XLSX)"}
                            value='xlsx'
                            onClick={handleDownload}
                        >
                            <CloudDownload />
                        </IconButton>
                    </Tooltip> : null
            }
            {
                hasPermission(permissions, 'device_dependent_setting_import') ?
                    <Tooltip title={translations["dependent_upload"] || "Upload dependent setting"}>
                        <IconButton
                            aria-label={translations["dependent_upload"] || "Upload dependent setting"}
                            id={'upload_device_dependent_setting'}
                            value='xlsx'
                            onClick={() => goToUploadDeviceDependentSetting()}
                        >
                            <CloudUpload />
                        </IconButton>
                    </Tooltip> : null
            }
        </div>
    };

    const handleDownload = async event => {
        let type = event.currentTarget.getAttribute('value');
        const objectURL = `${config.backend_domain}:${config.backend_port}/dependent_settings/export_all?Type=${type}&ActiveCustomerID=${activeCustomerID}`;
        let exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    };

    const handleBulkDelete = deviceDependentSettingsIDS => {
        setDeleteModalState({ ...deleteModalState, open: true, deviceDependentSettingsIDs: deviceDependentSettingsIDS });
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, deviceDependentSettingsIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteDeviceDependentSettings({ DeviceDependentSettingsIDs: deleteModalState.deviceDependentSettingsIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, deviceDependentSettingsIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["device_dependent_setting"] || "Device dependent setting"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(deviceDependentSettings => deviceDependentSettings.id))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        });
        objToSendColumn[`DeviceDependentSetting`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    };

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'device_dependent_setting_delete') ? (
                    <DeviceDependentSettingDelete
                        entityName={translations['device_dependent_setting'] || 'Device dependent setting'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleteModalState={deleteModalState}
                        deleting={deleting}
                    />
                ) : null
            }
        </Paper>
    )
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        deleting: state.admin.deviceDependentSettings.deleting,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        fetching: state.admin.deviceDependentSettings.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchDeviceDependentSettingsCount: (filter, options) => dispatch(actions.fetchDependentSettingCount(filter, options)),
        onFetchDeviceDependentSettings: (filter, options) => dispatch(actions.fetchDependentSetting(filter, options)),
        onBulkDeleteDeviceDependentSettings: data => dispatch(actions.bulkDeleteDependentSetting(data)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),

    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(DeviceDependentSettingList));